import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ZL_OCENENI_BEZ_SMLOUVY, type ZlOceneniTyp, type ZlSmer } from '@helfr/domain';
import { api, hlaskaZChyby } from '../../api/client.js';
import type {
  ImportBatch,
  ImportBatchDetail,
  Smlouva,
  SoupisPolozka,
  ZmenaDetail,
  ZmenovyList,
} from '../../api/types.js';
import { useAuth } from '../../auth/AuthContext.js';
import { formatKc } from '../../lib/format.js';
import { ZL_OCENENI_LABEL } from '../../lib/labels.js';
import { Btn, Chyba, Dialog, Input, Pole, Select, Textarea } from '../../components/ui.js';

/** Řádek položkového ZL v editoru — hodnoty jako stringy (peníze nikdy float). */
interface PolozkaRadek {
  popis: string;
  mj: string;
  mnozstvi: string;
  jednotkovaCena: string;
  soupisPraciPolozkaId: string;
}

interface KalkulaceRadekForm {
  profese: string;
  hodiny: string;
  materialPopis: string;
  materialCastka: string;
}

const PRAZDNA_POLOZKA: PolozkaRadek = {
  popis: '',
  mj: '',
  mnozstvi: '',
  jednotkovaCena: '',
  soupisPraciPolozkaId: '',
};
const PRAZDNY_KALKULACNI: KalkulaceRadekForm = {
  profese: '',
  hodiny: '',
  materialPopis: '',
  materialCastka: '',
};

/** Typy ocenění nabízené pro daný směr (výnos NIKDY vlastní kalkulace/ruční náklad). */
function typyProSmer(smer: ZlSmer): ZlOceneniTyp[] {
  return smer === 'vynos'
    ? ['sod_jednotkova_cena', 'cn_s_rozpadem']
    : ['cn_s_rozpadem', 'sod_jednotkova_cena', 'vlastni_kalkulace', 'rucni_naklad'];
}

/** Nápověda k typu ocenění (B3.5) — vysvětlí, o čem formulář rozhoduje. */
const OCENENI_HINT: Record<ZlOceneniTyp, string> = {
  sod_jednotkova_cena:
    'Cena ze soupisu prací (SOD) — jednotkovou cenu dosadí smlouva, nejde ji měnit.',
  cn_s_rozpadem:
    'Mimo soupis — cenu navrhnete sami a rozepíšete (materiál, práce); TDI ji posuzuje jako cenovou nabídku.',
  vlastni_kalkulace:
    'Naši lidé — zadáte profesi a hodiny, sazbu dosadí firma (v terénu se nezobrazuje).',
  rucni_naklad: 'Jednorázový náklad — doprava, jeřáb, skládka…',
};

/**
 * Editor Změnového listu (wireframe 13 §3) — obě strany, jeden formulář:
 * položky proti soupisu prací (cena ze SOD předvyplněná a u výnosu ZAMČENÁ,
 * méněpráce záporným množstvím) nebo volné položky (CN s rozpadem), na
 * nákladové straně navíc vlastní kalkulace (profese/hodiny — sazby dosadí
 * server, tady se NEZOBRAZUJÍ) a ruční náklad. Σ počítá VÝHRADNĚ server.
 */
export function ZlEditor({
  zmena,
  smer,
  zl,
  onZavrit,
}: {
  zmena: ZmenaDetail;
  smer: ZlSmer;
  /** Existující ZL = editace (PATCH), jinak založení (POST). */
  zl?: ZmenovyList;
  onZavrit: () => void;
}) {
  const { maPravo } = useAuth();
  const queryClient = useQueryClient();
  const [popis, setPopis] = useState(zl?.popis ?? zmena.popis);
  const [oceneniTyp, setOceneniTyp] = useState<ZlOceneniTyp>(
    zl?.oceneniTyp ?? (smer === 'vynos' ? 'sod_jednotkova_cena' : 'cn_s_rozpadem'),
  );
  const [smlouvaId, setSmlouvaId] = useState(zl?.smlouvaId ?? zl?.smlouva?.id ?? '');
  const [polozky, setPolozky] = useState<PolozkaRadek[]>(
    zl && zl.polozky.length > 0
      ? zl.polozky.map((p) => ({
          popis: p.popis,
          mj: p.mj,
          mnozstvi: p.mnozstvi,
          jednotkovaCena: p.jednotkovaCena ?? '',
          soupisPraciPolozkaId: p.soupisPraciPolozkaId ?? '',
        }))
      : [{ ...PRAZDNA_POLOZKA }],
  );
  const [kalkulace, setKalkulace] = useState<KalkulaceRadekForm[]>(
    zl && zl.vlastniKalkulace.length > 0
      ? zl.vlastniKalkulace.map((r) => ({
          profese: r.profese,
          hodiny: r.hodiny,
          materialPopis: r.materialPopis ?? '',
          materialCastka: r.materialCastka ?? '',
        }))
      : [{ ...PRAZDNY_KALKULACNI }],
  );
  const [chyba, setChyba] = useState<string | null>(null);

  const bezSmlouvy = (ZL_OCENENI_BEZ_SMLOUVY as readonly string[]).includes(oceneniTyp);
  const jeKalkulace = oceneniTyp === 'vlastni_kalkulace';

  const { data: smlouvy } = useQuery({
    queryKey: ['smlouvy', zmena.zakazkaId],
    queryFn: () => api.get<Smlouva[]>(`/api/contracts?zakazkaId=${zmena.zakazkaId}`),
    enabled: maPravo('smlouvy.read'),
  });
  const vhodneSmlouvy = (smlouvy ?? []).filter((s) =>
    smer === 'vynos' ? s.typ === 'hlavni' : s.typ === 'subdodavatelska',
  );

  // Výnosový ZL visí na hlavní SOD (invariant 4) — jediná volba se předvyplní.
  useEffect(() => {
    if (!zl && smer === 'vynos' && smlouvaId === '' && vhodneSmlouvy.length > 0) {
      setSmlouvaId(vhodneSmlouvy[0]?.id ?? '');
    }
    // závislost záměrně jen na smlouvy — jen předvyplnění po načtení
  }, [smlouvy]);

  // Platný soupis prací smlouvy — read-only ceník pro vazbu položek.
  const { data: batche } = useQuery({
    queryKey: ['importy', smlouvaId],
    queryFn: () => api.get<ImportBatch[]>(`/api/imports?smlouvaId=${smlouvaId}`),
    enabled: maPravo('import.read') && !!smlouvaId && !bezSmlouvy,
  });
  const platnyBatchId = batche?.find((b) => b.jePlatny)?.id;
  const { data: soupis } = useQuery({
    queryKey: ['import', platnyBatchId],
    queryFn: () => api.get<ImportBatchDetail>(`/api/imports/${platnyBatchId}`),
    enabled: !!platnyBatchId,
  });
  const soupisPolozky = soupis?.polozky ?? [];
  const soupisPodleId = new Map<string, SoupisPolozka>(soupisPolozky.map((p) => [p.id, p]));

  const uprav = (i: number, zmenaRadku: Partial<PolozkaRadek>) =>
    setPolozky((stare) => stare.map((r, j) => (j === i ? { ...r, ...zmenaRadku } : r)));
  const upravKalk = (i: number, zmenaRadku: Partial<KalkulaceRadekForm>) =>
    setKalkulace((stare) => stare.map((r, j) => (j === i ? { ...r, ...zmenaRadku } : r)));

  const uloz = useMutation({
    mutationFn: () => {
      const telo = {
        popis,
        oceneniTyp,
        smlouvaId: bezSmlouvy ? null : smlouvaId || null,
        ...(jeKalkulace
          ? {
              vlastniKalkulace: kalkulace.map((r) => ({
                profese: r.profese,
                hodiny: r.hodiny || '0',
                ...(r.materialPopis && { materialPopis: r.materialPopis }),
                ...(r.materialCastka && { materialCastka: r.materialCastka }),
              })),
            }
          : {
              polozky: polozky.map((p) => {
                const soupisItem = p.soupisPraciPolozkaId
                  ? soupisPodleId.get(p.soupisPraciPolozkaId)
                  : undefined;
                // Cena z vazby na soupis je ZAMČENÁ — neposílá se, dosadí ji server
                // (odchylka u výnosu = 422 z API).
                const cenaZamcena = smer === 'vynos' && soupisItem?.jednotkovaCena != null;
                return {
                  popis: p.popis,
                  mj: p.mj,
                  mnozstvi: p.mnozstvi,
                  ...(p.soupisPraciPolozkaId && {
                    soupisPraciPolozkaId: p.soupisPraciPolozkaId,
                  }),
                  ...(!cenaZamcena && p.jednotkovaCena && { jednotkovaCena: p.jednotkovaCena }),
                };
              }),
            }),
      };
      return zl
        ? api.patch<ZmenaDetail>(`/api/zmeny/${zmena.id}/zl/${zl.id}`, telo)
        : api.post<ZmenaDetail>(`/api/zmeny/${zmena.id}/zl`, { smer, ...telo });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['zmena', zmena.id] });
      onZavrit();
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  return (
    <Dialog
      nadpis={
        zl
          ? `Upravit ${zl.cislo ?? 'ZL'}`
          : smer === 'vynos'
            ? 'Nový výnosový ZL (k hlavní SOD, na investora)'
            : 'Nový nákladový ZL (subsmlouva / vlastní / ruční)'
      }
      siroky
      onZavrit={onZavrit}
    >
      <div className="flex flex-col gap-4">
        {chyba && <Chyba>{chyba}</Chyba>}
        <Pole popisek="Popis ZL" povinne>
          <Textarea
            aria-label="Popis ZL"
            rows={2}
            value={popis}
            onChange={(e) => setPopis(e.target.value)}
          />
        </Pole>
        <div className="grid gap-4 sm:grid-cols-2">
          <Pole popisek="Typ ocenění" povinne>
            <Select
              aria-label="Typ ocenění"
              value={oceneniTyp}
              onChange={(e) => setOceneniTyp(e.target.value as ZlOceneniTyp)}
            >
              {typyProSmer(smer).map((t) => (
                <option key={t} value={t}>
                  {ZL_OCENENI_LABEL[t]}
                </option>
              ))}
            </Select>
            <p className="mt-1 text-sm text-text-secondary">{OCENENI_HINT[oceneniTyp]}</p>
          </Pole>
          {!bezSmlouvy && (
            <Pole
              popisek={smer === 'vynos' ? 'Smlouva (hlavní SOD)' : 'Subdodavatelská smlouva'}
              povinne
            >
              <Select
                aria-label="Smlouva ZL"
                value={smlouvaId}
                onChange={(e) => setSmlouvaId(e.target.value)}
              >
                <option value="">(vyberte smlouvu)</option>
                {vhodneSmlouvy.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.cislo ?? '(bez čísla)'} — {s.subdodavatel?.nazev ?? s.protistrana}
                  </option>
                ))}
              </Select>
            </Pole>
          )}
          {bezSmlouvy && (
            <p className="self-end pb-3 text-sm text-text-secondary">
              Vlastní provedení GD — ZL je bez smlouvy.
            </p>
          )}
        </div>

        {jeKalkulace ? (
          <div className="flex flex-col gap-2">
            <h3 className="font-semibold">Řádky vlastní kalkulace</h3>
            <p className="text-sm text-text-secondary">
              Kč práce = hodiny × kalkulační sazba profese — dosadí server z číselníku sazeb.
            </p>
            {kalkulace.map((r, i) => (
              <div key={i} className="grid items-end gap-2 sm:grid-cols-[1fr_6rem_1fr_7rem_auto]">
                <Pole popisek={i === 0 ? 'Profese' : ''}>
                  <Input
                    aria-label={`Profese řádku ${i + 1}`}
                    placeholder="tesař"
                    value={r.profese}
                    onChange={(e) => upravKalk(i, { profese: e.target.value })}
                  />
                </Pole>
                <Pole popisek={i === 0 ? 'Hodiny' : ''}>
                  <Input
                    aria-label={`Hodiny řádku ${i + 1}`}
                    placeholder="32"
                    value={r.hodiny}
                    onChange={(e) => upravKalk(i, { hodiny: e.target.value })}
                  />
                </Pole>
                <Pole popisek={i === 0 ? 'Materiál — popis' : ''}>
                  <Input
                    aria-label={`Materiál řádku ${i + 1}`}
                    value={r.materialPopis}
                    onChange={(e) => upravKalk(i, { materialPopis: e.target.value })}
                  />
                </Pole>
                <Pole popisek={i === 0 ? 'Materiál Kč' : ''}>
                  <Input
                    aria-label={`Materiál Kč řádku ${i + 1}`}
                    placeholder="51000"
                    value={r.materialCastka}
                    onChange={(e) => upravKalk(i, { materialCastka: e.target.value })}
                  />
                </Pole>
                <Btn
                  size="desktop"
                  varianta="secondary"
                  aria-label={`Odebrat řádek ${i + 1}`}
                  disabled={kalkulace.length === 1}
                  onClick={() => setKalkulace((s) => s.filter((_, j) => j !== i))}
                >
                  ×
                </Btn>
              </div>
            ))}
            <Btn
              size="desktop"
              varianta="secondary"
              className="self-start"
              onClick={() => setKalkulace((s) => [...s, { ...PRAZDNY_KALKULACNI }])}
            >
              + Přidat řádek
            </Btn>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <h3 className="font-semibold">Položky ZL</h3>
            <p className="text-sm text-text-secondary">
              Položka v rozpočtu → jednotková cena ze SOD (předvyplněná a zamčená); méněpráce se
              zapisuje záporným množstvím. Mimo soupis = CN s rozpadem, cena volná.
            </p>
            {polozky.map((p, i) => {
              const soupisItem = p.soupisPraciPolozkaId
                ? soupisPodleId.get(p.soupisPraciPolozkaId)
                : undefined;
              const cenaZamcena = smer === 'vynos' && soupisItem?.jednotkovaCena != null;
              const zaporna = p.mnozstvi.trim().startsWith('-');
              return (
                <div
                  key={i}
                  className={`grid items-end gap-2 rounded-lg p-1 sm:grid-cols-[1fr_1fr_5rem_6rem_8rem_auto] ${zaporna ? 'bg-error-bg' : ''}`}
                >
                  {soupisPolozky.length > 0 ? (
                    <Pole popisek={i === 0 ? 'Vazba na soupis prací' : ''}>
                      <Select
                        aria-label={`Vazba na soupis položky ${i + 1}`}
                        value={p.soupisPraciPolozkaId}
                        onChange={(e) => {
                          const item = soupisPodleId.get(e.target.value);
                          uprav(i, {
                            soupisPraciPolozkaId: e.target.value,
                            ...(item && {
                              popis: item.popis,
                              mj: item.jednotka,
                              jednotkovaCena: item.jednotkovaCena ?? '',
                            }),
                          });
                        }}
                      >
                        <option value="">mimo soupis (CN s rozpadem)</option>
                        {soupisPolozky.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.kod} — {s.popis}
                          </option>
                        ))}
                      </Select>
                    </Pole>
                  ) : (
                    <span className="self-center text-sm text-text-secondary">
                      {i === 0 ? 'Bez soupisu — volné položky (CN s rozpadem).' : ''}
                    </span>
                  )}
                  <Pole popisek={i === 0 ? 'Popis' : ''}>
                    <Input
                      aria-label={`Popis položky ${i + 1}`}
                      value={p.popis}
                      onChange={(e) => uprav(i, { popis: e.target.value })}
                    />
                  </Pole>
                  <Pole popisek={i === 0 ? 'MJ' : ''}>
                    <Input
                      aria-label={`MJ položky ${i + 1}`}
                      placeholder="ks"
                      value={p.mj}
                      onChange={(e) => uprav(i, { mj: e.target.value })}
                    />
                  </Pole>
                  <Pole popisek={i === 0 ? 'Množství' : ''}>
                    <Input
                      aria-label={`Množství položky ${i + 1}`}
                      placeholder="12 / −3"
                      value={p.mnozstvi}
                      onChange={(e) => uprav(i, { mnozstvi: e.target.value })}
                    />
                  </Pole>
                  <Pole popisek={i === 0 ? 'Jedn. cena Kč' : ''}>
                    <Input
                      aria-label={`Jednotková cena položky ${i + 1}`}
                      value={cenaZamcena ? (soupisItem?.jednotkovaCena ?? '') : p.jednotkovaCena}
                      disabled={cenaZamcena}
                      title={
                        cenaZamcena
                          ? 'Cena je daná smlouvou o dílo (SOD) — proto ji nejde přepsat. Jinou cenu řešte položkou mimo soupis.'
                          : undefined
                      }
                      onChange={(e) => uprav(i, { jednotkovaCena: e.target.value })}
                    />
                  </Pole>
                  <Btn
                    size="desktop"
                    varianta="secondary"
                    aria-label={`Odebrat položku ${i + 1}`}
                    disabled={polozky.length === 1}
                    onClick={() => setPolozky((s) => s.filter((_, j) => j !== i))}
                  >
                    ×
                  </Btn>
                  {zaporna && (
                    <p className="text-sm font-semibold text-error-text sm:col-span-full">
                      méněpráce (záporné množství)
                    </p>
                  )}
                </div>
              );
            })}
            <Btn
              size="desktop"
              varianta="secondary"
              className="self-start"
              onClick={() => setPolozky((s) => [...s, { ...PRAZDNA_POLOZKA }])}
            >
              + Přidat položku
            </Btn>
          </div>
        )}

        {/* Žargon „režim PDP § 92e" pryč z plátna — jen „ceny bez DPH" (blueprint 16). */}
        <p className="text-sm text-text-secondary">
          Σ ZL spočítá server po uložení (ceny bez DPH).
          {zl?.castka !== undefined && ` Aktuálně: ${formatKc(zl.castka)}.`}
        </p>
        <div className="flex gap-3">
          <Btn
            size="desktop"
            disabled={uloz.isPending || popis.trim() === ''}
            onClick={() => uloz.mutate()}
          >
            {uloz.isPending ? 'Ukládám…' : zl ? 'Uložit ZL' : 'Založit ZL'}
          </Btn>
          <Btn size="desktop" varianta="secondary" onClick={onZavrit}>
            Zrušit
          </Btn>
        </div>
      </div>
    </Dialog>
  );
}
